import Stack from 'components/elements/Stack'
import SubjectCard from 'components/modules/Subjects/SubjectCard'
import { GroupedCategory } from 'interfaces/Subject'
import { parseLocale } from 'lib/constants/localization'
import { appSearchRouteOptions, searchResultRouteOptions } from 'lib/constants/routes'
import { getGroupedSubjectCategories, getSubjectLanguageOptions, sortSubjects } from 'lib/formatting/subjects'
import _useTranslation from 'lib/hooks/_useTranslation'
import { useRouter } from 'next/dist/client/router'
import Link from 'next/link'
import { useState, useEffect, useMemo, useCallback } from 'react'
import { ApiError } from 'services/ApiService'
import SubjectService from 'services/SubjectService'
import { Body1, Body2, ButtonText } from 'stories/elements/Typography/Text'
import styled from 'styled-components'
import Spinner from 'components/elements/Spinner'
import { ExamType } from 'interfaces/ExamType'
import { LanguageInterface } from 'interfaces/Language'
import { getSelectableExamTypes } from 'lib/formatting/examType'
import CountryService from 'services/CountryService'
import { ChipCard, ChipWrapper } from 'components/modules/UploadDetails/SubjectSelection/SubjectSelection'
import theme from 'stories/utils/theme'
import Close from 'assets/icons/round_close.svg'

const Container = styled.div`
    display: grid;
    place-content: center;
    gap: 30px;
    a {
        width: 225px;
    }
`

const SubjectGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
    margin: 0 auto;

    @media (max-width: 480px) {
        grid-template-columns: 1fr;
    }

    & a {
        display: grid;
    }
`

interface Props {
    isDarkTheme?: boolean
}

const SubjectsKnow = ({ isDarkTheme }: Props) => {
    const [groupedCategories, setGroupedCategories] = useState<GroupedCategory[]>()
    const router = useRouter()
    const parsedLocale = parseLocale(router.locale)
    const { t: tCommon } = _useTranslation('common')
    const { t } = _useTranslation('knows')
    const [selectedExamType, setSelectedExamType] = useState<ExamType | null | undefined>()
    const [examTypes, setExamTypes] = useState<ExamType[]>()
    const [defaultExamType, setDefaultExamType] = useState<ExamType | null>(null)
    const [selectedLanguage, setSelectedLanguage] = useState<LanguageInterface>()

    const languageOptions = useMemo(() => getSubjectLanguageOptions(groupedCategories), [groupedCategories])

    const fetchExamTypes = useCallback(() => {
        CountryService.listExamTypes(parsedLocale.countryCode)
            .then((examTypes) => {
                const defaultExamType = examTypes?.length ? examTypes.find((exam) => exam.name === 'DEFAULT') : null
                defaultExamType && setDefaultExamType(defaultExamType)
                setSelectedExamType(defaultExamType)
                setExamTypes(getSelectableExamTypes(examTypes))
            })
            .catch((error) => {
                if (error instanceof ApiError) {
                    error.handleUnknownError(tCommon, 'subjects.list')
                } else {
                    throw error
                }
            })
    }, [parsedLocale.countryCode])

    useEffect(() => {
        fetchExamTypes()
    }, [fetchExamTypes])

    const fetchSubjects = useCallback(() => {
        if (selectedExamType === undefined) return

        SubjectService.list(parsedLocale.countryCode, selectedExamType)
            .then((subjects) => {
                const groupedCategories = getGroupedSubjectCategories(sortSubjects(subjects))
                setGroupedCategories(groupedCategories)
            })
            .catch((error) => {
                if (error instanceof ApiError) {
                    error.handleUnknownError(tCommon, 'subjects.list')
                } else {
                    throw error
                }
            })
    }, [selectedExamType, parsedLocale.countryCode])

    useEffect(() => {
        fetchSubjects()
    }, [fetchSubjects])

    const isLanguageSelected = (language: LanguageInterface) => selectedLanguage && selectedLanguage.id === language.id

    const onLanguageSelected = (language: LanguageInterface) => setSelectedLanguage(language)

    if (!groupedCategories?.length) return <Spinner forceShow />

    const filterCategories = (options: GroupedCategory[]) =>
        selectedLanguage?.id
            ? options
                  .filter((option) => option.languages.map(({ id }) => id).includes(selectedLanguage.id))
                  .map((option) => {
                      const { subjects, ...rest } = option
                      const filteredSubjects = subjects.filter(
                          (subject) => subject.data.language.name === selectedLanguage.name
                      )
                      return { subjects: filteredSubjects, ...rest }
                  })
            : options

    const hasExamBoards = examTypes?.length

    const showLanguageDropdown = languageOptions?.length > 1

    const isExamTypeSelected = selectedExamType && selectedExamType.name !== 'DEFAULT'

    const changeExamType = (examType: ExamType | null) => setSelectedExamType(examType)

    return (
        <Container>
            <Stack>
                {showLanguageDropdown ? (
                    <ChipWrapper removePadding>
                        <ButtonText fontWeight={400} color={theme.colors.body2Black} fontFamily="var(--font-inter)">
                            {t('knows/createDetailsPageKnowLanguageLabel')}
                        </ButtonText>
                        <div className="types">
                            {languageOptions?.map(({ data, label }) => (
                                <ChipCard
                                    key={data.id}
                                    onClick={() => onLanguageSelected(data)}
                                    subjectColor={
                                        isLanguageSelected(data) ? theme.colors.deepSeaBlue : theme.colors.placeholderText
                                    }
                                >
                                    <Body2 color={theme.colors.deepSeaBlue} fontFamily="var(--font-inter)" fontWeight={400}>
                                        {label}
                                    </Body2>
                                </ChipCard>
                            ))}
                        </div>
                    </ChipWrapper>
                ) : null}
                {hasExamBoards ? (
                    <ChipWrapper removePadding>
                        {isExamTypeSelected ? (
                            <>
                                <ButtonText fontWeight={400} color={theme.colors.body2Black} fontFamily="var(--font-inter)">
                                    {t('knows/selectedExamTypeLabel')}
                                </ButtonText>
                                <ChipCard onClick={() => changeExamType(defaultExamType)}>
                                    <Body2 color={theme.colors.deepSeaBlue} fontFamily="var(--font-inter)" fontWeight={400}>
                                        {selectedExamType.name}
                                    </Body2>
                                    <Close />
                                </ChipCard>
                            </>
                        ) : (
                            <>
                                <ButtonText
                                    fontWeight={400}
                                    color={theme.colors.backgroundSecondary}
                                    fontFamily="var(--font-inter)"
                                >
                                    {t('knows/selectedExamTypeFilterLabel')}
                                </ButtonText>
                                <div className="types">
                                    {examTypes.map((exam) => (
                                        <ChipCard key={exam.uuid} onClick={() => changeExamType(exam)}>
                                            <Body2
                                                color={theme.colors.actionGray}
                                                fontFamily="var(--font-inter)"
                                                fontWeight={400}
                                            >
                                                {exam.name}
                                            </Body2>
                                        </ChipCard>
                                    ))}
                                </div>
                            </>
                        )}
                    </ChipWrapper>
                ) : null}
            </Stack>
            {filterCategories(groupedCategories).map(({ subjects, category }) => (
                <Stack key={category}>
                    <Body1 color={isDarkTheme ? theme.colors.white : theme.colors.placeholderText}>{category}</Body1>
                    <SubjectGrid>
                        {subjects.map(({ data: subject }) => (
                            <Link href={searchResultRouteOptions('', subject.id)} key={subject.id} prefetch={false}>
                                <SubjectCard
                                    subject={subject}
                                    to={appSearchRouteOptions(subject.name, subject.id)}
                                    isDarkTheme={isDarkTheme}
                                />
                            </Link>
                        ))}
                    </SubjectGrid>
                </Stack>
            ))}
        </Container>
    )
}

export default SubjectsKnow
